import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import type { RecordAccessContext, RecordAccessRequestItem } from "../../services/record.service";

type AccessRequestPanelProps = {
  context: RecordAccessContext;
  inviteToken: string;
  submitting: boolean;
  onSubmit: (message?: string) => void;
};

const accessStateLabel: Record<string, { label: string; color: "default" | "warning" | "success" | "error" }> = {
  none: { label: "Erişim yok", color: "default" },
  pending: { label: "Onay bekleniyor", color: "warning" },
  rejected: { label: "Reddedildi", color: "error" },
  granted: { label: "Erişim var", color: "success" },
};

export function AccessRequestPanel({
  context,
  inviteToken: _inviteToken,
  submitting,
  onSubmit,
}: AccessRequestPanelProps) {
  const state = accessStateLabel[context.access_state] ?? accessStateLabel.none;

  return (
    <Card sx={{ p: 3, maxWidth: 560 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon icon="mdi:lock-outline" width={24} />
          <Typography variant="h6">Kayıt erişimi gerekli</Typography>
        </Stack>

        <Typography variant="body1">
          <strong>{context.record_name}</strong>
        </Typography>

        {context.owner_display_name || context.owner_email ? (
          <Typography variant="body2" color="text.secondary">
            Kayıt sahibi: {context.owner_display_name ?? context.owner_email}
            {context.owner_email && context.owner_display_name
              ? ` (${context.owner_email})`
              : ""}
          </Typography>
        ) : null}

        <Chip label={state.label} color={state.color} size="small" sx={{ alignSelf: "flex-start" }} />

        {context.access_state === "none" && context.can_request ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Bu kayda erişiminiz yok. Kayıt sahibinden erişim isteyebilirsiniz.
            </Typography>
            <AccessRequestForm submitting={submitting} onSubmit={onSubmit} />
          </>
        ) : null}

        {context.access_state === "pending" ? (
          <Alert severity="info">
            Erişim isteğiniz kayıt sahibine iletildi. Onaylandığında bu sayfayı yenileyerek kaydı
            görüntüleyebilirsiniz.
          </Alert>
        ) : null}

        {context.access_state === "rejected" && context.can_request ? (
          <>
            <Alert severity="warning">Önceki erişim isteğiniz reddedildi. Tekrar isteyebilirsiniz.</Alert>
            <AccessRequestForm submitting={submitting} onSubmit={onSubmit} />
          </>
        ) : null}

        {context.access_state === "rejected" && !context.can_request ? (
          <Alert severity="error">Erişim isteğiniz reddedildi.</Alert>
        ) : null}
      </Stack>
    </Card>
  );
}

type AccessDeniedPanelProps = {
  error?: "forbidden" | "not_found" | "unknown";
};

export function AccessDeniedPanel({ error = "forbidden" }: AccessDeniedPanelProps) {
  const unknown = error === "unknown";

  if (unknown) {
    return (
      <Card sx={{ p: 3, maxWidth: 560, width: "100%" }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon icon="mdi:alert-circle-outline" width={24} />
            <Typography variant="h6">Kayıt yüklenemedi</Typography>
          </Stack>
          <Alert severity="warning">
            Kayıt bilgisi alınırken bir hata oluştu. Lütfen daha sonra tekrar deneyin.
          </Alert>
          <Box>
            <Button variant="outlined" component={RouterLink} to="/dashboard/app">
              Dosyalara dön
            </Button>
          </Box>
        </Stack>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, maxWidth: 560, width: "100%" }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon icon="mdi:file-remove-outline" width={24} />
          <Typography variant="h6">Böyle bir kayıt yok</Typography>
        </Stack>

        <Alert severity="info">
          Aradığınız kayıt mevcut değil veya kaldırılmış olabilir. Bağlantıyı kontrol edip
          tekrar deneyin.
        </Alert>

        <Box>
          <Button variant="outlined" component={RouterLink} to="/dashboard/app">
            Dosyalara dön
          </Button>
        </Box>
      </Stack>
    </Card>
  );
}

function AccessRequestForm({
  submitting,
  onSubmit,
}: {
  submitting: boolean;
  onSubmit: (message?: string) => void;
}) {
  const [message, setMessage] = useState("");

  return (
    <Stack spacing={2}>
      <TextField
        label="Mesaj (opsiyonel)"
        multiline
        minRows={2}
        fullWidth
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <Box>
        <Button
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
          onClick={() => onSubmit(message.trim() || undefined)}
        >
          Erişim iste
        </Button>
      </Box>
    </Stack>
  );
}

type OwnerAccessRequestsPanelProps = {
  requests: RecordAccessRequestItem[];
  highlightRequestId?: number | null;
  actingId: number | null;
  bulkActing?: boolean;
  onApprove: (requestId: number) => void;
  onReject: (requestId: number) => void;
  onBulkApprove?: () => void;
  onBulkReject?: () => void;
};

export function OwnerAccessRequestsPanel({
  requests,
  highlightRequestId,
  actingId,
  bulkActing = false,
  onApprove,
  onReject,
  onBulkApprove,
  onBulkReject,
}: OwnerAccessRequestsPanelProps) {
  const pending = requests.filter((r) => r.status === "Pending");
  if (pending.length === 0) return null;

  return (
    <Card sx={{ p: 3, mb: 3, border: highlightRequestId ? "1px solid" : undefined, borderColor: "primary.main" }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6">Bekleyen erişim istekleri ({pending.length})</Typography>
          {pending.length > 1 && onBulkApprove && onBulkReject ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={bulkActing || actingId !== null}
                onClick={onBulkApprove}
              >
                Tümünü onayla
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={bulkActing || actingId !== null}
                onClick={onBulkReject}
              >
                Tümünü reddet
              </Button>
            </Stack>
          ) : null}
        </Stack>
        {pending.map((req) => (
          <Box
            key={req.id}
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: req.id === highlightRequestId ? "action.selected" : "action.hover",
            }}
          >
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                {req.requester_name ?? req.requester_email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {req.requester_email}
              </Typography>
              {req.message ? (
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  &ldquo;{req.message}&rdquo;
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  disabled={actingId === req.id}
                  onClick={() => onApprove(req.id)}
                >
                  Onayla
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={actingId === req.id}
                  onClick={() => onReject(req.id)}
                >
                  Reddet
                </Button>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}
